import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {
  Action,
  CountBy,
  EntrySectionUpdateEvent,
  MetricEntryBehavior,
} from 'common/query-builder/query-entry-section/types';
import {QueryOptionsChangeEvent} from 'common/query-builder/conversion-query-options/types';
import {mapEventEntryToBookmarkExclusionStep} from 'common/report/funnels/models/format';
import {MetricEntry, QueryEventEntry} from 'common/query-builder/query-entry-section/types';
import {BaseEntryRow} from 'common/query-builder/query-entry-section/row-types/base';
import {RangeSelection} from 'common/widgets/step-range-selector/types';
import {MetricType} from 'common/query-builder/query-block/constants';

import {FunnelsMetricEntryRowEl} from './template';

export class FunnelsMetricEntryRow extends BaseEntryRow<MetricEntry> {
  template = FunnelsMetricEntryRowEl;

  isPlaceholder() {
    const behaviors = this.entry.behavior.behaviors || [];
    return !this.hasValidSteps() || behaviors.some((behavior) => !this.hasValidFilters(behavior));
  }

  hasValidSteps() {
    const behaviors = this.entry.behavior.behaviors || [];
    return behaviors.length >= 2 || behaviors.every((behavior) => !!behavior.name);
  }

  hasValidFilters(behavior: MetricEntryBehavior) {
    if (!behavior.filters) {
      return true;
    }
    return behavior.filters.every((filter) => new PropertyFilter(filter).isValid());
  }

  getFunnelsSpecificBehavior() {
    const behavior = this.entry.behavior;
    const {funnelOrder, exclusions, aggregateBy, conversionWindowUnit, conversionWindowDuration} = behavior;
    return {
      funnelOrder,
      exclusions,
      aggregateBy,
      conversionWindowUnit,
      conversionWindowDuration,
    };
  }

  handleEntryUpdate = (ev: EntrySectionUpdateEvent<QueryEventEntry>) => {
    const {entries} = ev.detail;
    const behaviors: MetricEntryBehavior[] = entries.map((entry) => ({
      id: entry.event.custom ? entry.event.id : null,
      type: entry.event.custom ? MetricType.CustomEvent : MetricType.Event,
      name: entry.event.value,
      filters: entry.filters,
      filtersOperator: entry.filtersOperator,
    }));
    const entry: MetricEntry = {
      ...this.entry,
      behavior: {
        ...this.entry.behavior,
        behaviors,
      },
    };
    this.section.updateEntries(Action.Edit, {entry, index: this.index});
  };

  handleFunnelsConversionCriterialChanged = (ev: QueryOptionsChangeEvent) => {
    const {exclusionSteps, funnelOrder, holdingPropertyConstant, conversionWindow} = ev.detail.options;
    const behavior = {...this.entry.behavior};

    if (exclusionSteps) {
      behavior.exclusions = exclusionSteps.map((step) => {
        const subBehaviors = behavior.behaviors;
        return mapEventEntryToBookmarkExclusionStep(subBehaviors.length, step);
      });
    }
    if (funnelOrder) {
      behavior.funnelOrder = funnelOrder;
    }
    if (holdingPropertyConstant) {
      behavior.aggregateBy = holdingPropertyConstant;
    }
    if (conversionWindow) {
      behavior.conversionWindowDuration = conversionWindow.value;
      behavior.conversionWindowUnit = conversionWindow.unit;
    }

    const entry = {
      ...this.entry,
      behavior,
    };
    this.section.updateEntries(Action.Edit, {entry, index: this.index});
  };

  handleFunnelsCountBySelected = (ev: CustomEvent<{item: CountBy<RangeSelection>}>) => {
    const countBy = ev.detail.item;
    const {advancedMenuValue} = countBy;

    const entry = {
      ...this.entry,
      measurement: {
        ...this.entry.measurement,
        aggregationOperator: countBy.aggregationOperator,
        ...(countBy?.property ? {property: countBy.property} : {}),
        stepIndex: advancedMenuValue?.isEntireRange ? null : advancedMenuValue?.range?.from,
      },
    };
    this.section.updateEntries(Action.Edit, {entry, index: this.index});
  };
}
